import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../redux/hook';
import { showMessageModal } from '../../redux/actions';
import Spinner from '../Spinner';
import HorairesFooter from '../HorairesFooter';
import { ModalCard, Divider, Button, getImage } from '@dishopsaas/design-system';
import { Typography, Box } from '@mui/material';
import getModalStyles from '../ModalBody/ModalBody.styles';
import ModalBody from '../ModalBody/ModalBody.view';
import { SECONDARY, PRIMARY } from '../../constants';
import { SxProps, Theme } from '@mui/material';

interface AppState {
  configurationReducer: {
    secondaryColor: string;
    name: string;
    onClickAction?: () => void;
  };
  componentReducer: {
    messageModal: string | null;
    dataModal?: {
      onClick?: () => Promise<void>;
      showCancel?: boolean;
      text?: string;
      title?: string;
      subTitle?: string;
      image?: string;
      withImage?: boolean;
      buttonName?: string;
      discountMessage?: string;
      hours?: any;
      buttonCancelName?: string;
      activationRange?: boolean;
      primaryColor?: string;
      secondaryColor?: string;
      primaryButtonSx?: SxProps<Theme>;
      secondaryButtonSx?: SxProps<Theme>;
      maxWidth?: boolean
    };
  };
}

const MessageModal: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const modalStyles = getModalStyles()
  const { messageModal, dataModal } = useAppSelector((state: AppState) => state.componentReducer);
  const { name } = useAppSelector((state: AppState) => state.configurationReducer);

  const {
    onClick,
    showCancel,
    text,
    title,
    subTitle,
    image,
    withImage,
    buttonName,
    discountMessage,
    hours,
    buttonCancelName,
    activationRange = false,
    primaryColor = PRIMARY,
    secondaryColor = SECONDARY,
    secondaryButtonSx,
    primaryButtonSx,
    maxWidth,
  } = dataModal || {};

  const handlePrimaryAction = async () => {
    if (onClick) {
      try {
        setLoading(true);
        await onClick();
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    } else {
      dispatch(showMessageModal(null));
    }
  };

  const handleSecondaryAction = () => {
    dispatch(showMessageModal(null));
  };

  return (
    <ModalCard open={messageModal !== null} maxWidth={maxWidth} sxModal={{ p: 0 }} onClose={handleSecondaryAction}>
      <ModalBody
        title={text || name}
        onClose={handleSecondaryAction}
        footer={
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              ...(loading
                ? {
                  justifyContent: 'center',
                  alignItems: 'center',
                }
                : {
                  gap: 4,
                }),
            }}
          >
            {loading ? (
              <Spinner />
            ) : (
              <>
                {showCancel && (
                  <Button
                    label={buttonCancelName || 'Annuler'}
                    colorType={secondaryColor}
                    onClick={handleSecondaryAction}
                    sx={{ flex: 1, ...secondaryButtonSx }}
                    size="large"
                  />
                )}

                <Button
                  label={buttonName || 'Ok'}
                  colorType={primaryColor}
                  onClick={handlePrimaryAction}
                  sx={{ flex: 1, ...primaryButtonSx }}
                  size="large"
                />
              </>
            )}
          </Box>
        }
        sxBody={withImage && { padding: 10 }}
      >
        <>
          {withImage ? (
            <>
              {image && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {getImage(image, { size: 80 }, 'none')}
                </Box>
              )}

              <Box sx={modalStyles.image}>
                {title && (
                  <Typography variant="h5" align="center">
                    {title}
                  </Typography>
                )}

                {subTitle && (
                  <Typography variant="body1" align="center">
                    {subTitle}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <>
              {discountMessage && (
                <>
                  <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                    {discountMessage}
                  </Typography>
                  <Divider />
                </>
              )}

              <Box>
                <Typography variant="body1" sx={{ fontWeight: 400 }}>
                  {messageModal}
                </Typography>
              </Box>

              {hours && (
                <Box sx={{ mt: 3 }}>
                  <HorairesFooter
                    hours={hours}
                    activationRange={activationRange}
                  />
                </Box>
              )}
            </>
          )}
        </>
      </ModalBody>
    </ModalCard>
  );
};

export default MessageModal;